import { BillingSetting, BillingSettingException, FiscalRates } from "../../models/BillingSetting";

/**
 * Alíquotas do item pela parametrização (regra do Gett): a exceção mais específica que bate com
 * NCM / UF do cliente / cliente substitui a parametrização (campo vazio na exceção = herda).
 * Exceção por produto não é aplicada (o simulador não escolhe produto do Gett).
 */
export interface FiscalContext {
  ncm?: string;
  /** UF da filial do cliente. */
  state?: string | null;
  customerName?: string | null;
}

export interface ResolvedRates {
  rates: FiscalRates;
  exception: BillingSettingException | null;
}

/** Maiúsculas, sem acento, só letras/dígitos. */
export const normalizeName = (value?: string | null) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();

const digits = (value?: string | null) => (value ?? "").replace(/\D/g, "");

const customerMatches = (exceptionName: string, customerName: string) => {
  const a = normalizeName(exceptionName);
  const b = normalizeName(customerName);
  // Mínimo de 4 caracteres (igual SaleBuilder): nome curto/genérico ("SA", "LTDA") casaria com qualquer cliente
  return a.length >= 4 && b.length >= 4 && (a.includes(b) || b.includes(a));
};

/** Pontuação da exceção (−1 = não se aplica): cliente 4, UF 2, NCM 1. */
export const exceptionScore = (exception: BillingSettingException, context: FiscalContext): number => {
  if (exception.productDescription) return -1;
  let score = 0;
  if (exception.ncm) {
    if (digits(exception.ncm) !== digits(context.ncm)) return -1;
    score += 1;
  }
  if (exception.state) {
    if ((context.state ?? "").toUpperCase() !== exception.state.toUpperCase()) return -1;
    score += 2;
  }
  if (exception.customerName) {
    if (!context.customerName || !customerMatches(exception.customerName, context.customerName)) return -1;
    score += 4;
  }
  return score === 0 ? -1 : score;
};

export const mergeRates = (base: FiscalRates, override?: FiscalRates | null): FiscalRates => {
  const merged: FiscalRates = { ...base };
  if (!override) return merged;
  (Object.keys(override) as (keyof FiscalRates)[]).forEach((key) => {
    const value = override[key];
    if (value !== null && value !== undefined && value !== "") (merged as any)[key] = value;
  });
  return merged;
};

export const resolveRates = (setting: BillingSetting, context: FiscalContext): ResolvedRates => {
  let best: BillingSettingException | null = null;
  let bestScore = -1;
  for (const exception of setting.exceptions ?? []) {
    const score = exceptionScore(exception, context);
    if (score > bestScore) {
      best = exception;
      bestScore = score;
    }
  }
  return { rates: mergeRates(setting.rates ?? {}, best?.rates), exception: best };
};

/** Recolhimento efetivo do ICMS (regra SC/TTD): ICMS destacado × (1 − crédito presumido). */
export const collectedIcmsRate = (rates: FiscalRates) => {
  const icms = effectiveIcmsRate(rates);
  return icms * (1 - (rates.presumedCreditPercent ?? 0) / 100);
};

/** ICMS destacado sobre a base cheia: alíquota × (1 − redução da base). */
export const effectiveIcmsRate = (rates: FiscalRates) =>
  (rates.icmsPercent ?? 0) * (1 - (rates.icmsBaseReductionPercent ?? 0) / 100);

/** Item com ST: MVA e alíquota interna informadas. */
export const hasSt = (rates: FiscalRates) => (rates.mvaPercent ?? 0) > 0 && (rates.stInternalPercent ?? 0) > 0;
